import { CollectionReference, DocumentReference } from "@firebase/firestore-types";
import * as moment from "moment";
import { IAccount, ICompany } from "../accounts/interfaces";

interface IAccountInformationAttributes {
  accountholdername: string;
  accountnumber: string;
  customernamekey: string;
  serviceaddress: string;
  servicecitystate: string;
  servicereference: string;
}
interface ICompanyAttributes {
  companyname: string;
  companyaddress: string;
  companycitystate: string;
  companyemail: string;
  companyfax: string;
  companyphone: string;
  companyweblink: string;
}
interface ICompanyInformation {
  _attributes: ICompanyAttributes;
}
interface IAccountInformation {
  _attributes: IAccountInformationAttributes;
}

interface IPowerAttributes {
  budgetpayment: string;
  cumulativeestimatedpower: string;
  cumulativeguaranteedpower: string;
  leasepayment: string;
  productionanniversary: string;
}

interface IPower {
  _attributes: IPowerAttributes;
}

interface IGuaranteedPowerArray {
  power: IPower[];
}

interface IServiceChargeAttributes {
  iscustomerservicecharge: string;
  servicecharge: string;
  servicelabel: string;
  servicerate: string;
  totalservicecharge: string;
}

interface IServiceCharges {
  servicecharge: ISingleServiceCharge[];
}

interface ISingleServiceCharge {
  _attributes: IServiceChargeAttributes;
}

interface IReadingAttributes {
  readdate: string;
  reading: string;
}

interface IGridReading {
  _attributes: IReadingAttributes;
}

interface IStatementAttributes {
  NumberYearsSolar: string;
  adjustment: string;
  duedate: string;
  solargenerated: string;
  statementdate: string;
  solarCredits: number;
}

interface IStatement {
  delivery: IServiceCharges;
  gridreading: IGridReading;
  solarreading: IGridReading;
  supply: IServiceCharges;
  _attributes: IStatementAttributes;
}

interface IStatements {
  statement: IStatement[];
}

interface ICSRAccount {
  accountinformation: IAccountInformation;
  delivery: ICompanyInformation;
  guaranteedpower: IGuaranteedPowerArray;
  statements: IStatements;
  supply: ICompanyInformation;
}

interface ICSRFile {
  account: ICSRAccount;
}

interface IBillAmountDue {
  id: string;
  totalAmountDue: number;
}

const TIMEOUT_SHORT = 250;

export function importCSRXml(
  projectDocument: DocumentReference,
  statementsCollection: CollectionReference,
  csr: ICSRFile
): Promise<any> {
  return new Promise<any>(resolve => {
    console.log(csr);
    console.log(projectDocument.id);
    const account: IAccount = {
      accountHolder: csr.account.accountinformation._attributes.accountholdername,
      accountNumber: csr.account.accountinformation._attributes.accountnumber,
      address: csr.account.accountinformation._attributes.serviceaddress,
      cityState: csr.account.accountinformation._attributes.servicecitystate,
      nameKey: csr.account.accountinformation._attributes.customernamekey,
      serviceReference: csr.account.accountinformation._attributes.servicereference,
    };

    const supplyCompany: ICompany = {
      address: csr.account.supply._attributes.companyaddress,
      cityState: csr.account.supply._attributes.companycitystate,
      email: csr.account.supply._attributes.companyemail,
      fax: csr.account.supply._attributes.companyfax,
      name: csr.account.supply._attributes.companyname,
      phone: csr.account.supply._attributes.companyphone,
      website: csr.account.supply._attributes.companyweblink,
    };

    const deliveryCompany: ICompany = {
      address: csr.account.delivery._attributes.companyaddress,
      cityState: csr.account.delivery._attributes.companycitystate,
      email: csr.account.delivery._attributes.companyemail,
      fax: csr.account.delivery._attributes.companyfax,
      name: csr.account.delivery._attributes.companyname,
      phone: csr.account.delivery._attributes.companyphone,
      website: csr.account.delivery._attributes.companyweblink,
    };

    const guaranteedPowerTable: CollectionReference = projectDocument.collection(
      "guaranteedPowerTable"
    );

    guaranteedPowerTable
      .orderBy("productionAnniversary")
      .get()
      .then(querySnapshot => {
        querySnapshot.forEach(doc => {
          if (doc.exists) {
            guaranteedPowerTable.doc(doc.id).delete();
          }
        });
      })
      .then(() => {
        csr.account.guaranteedpower.power.forEach(power => {
          const averageEstimatedPower = calculateAverageEstimatedPower(
            Number(power._attributes.productionanniversary),
            Number(power._attributes.cumulativeestimatedpower)
          );
          const averageGuaranteedPower = calculateAverageGuaranteedPower(
            Number(power._attributes.productionanniversary),
            Number(power._attributes.cumulativeguaranteedpower)
          );
          guaranteedPowerTable.add({
            productionAnniversary: Number(power._attributes.productionanniversary),
            cumulativeEstimatedPower: Number(power._attributes.cumulativeestimatedpower),
            cumulativeGuaranteedPower: Number(power._attributes.cumulativeguaranteedpower),
            averageEstimatedPower,
            averageGuaranteedPower,
            leasePayment: Number(power._attributes.leasepayment),
            budgetPayment: Number(power._attributes.budgetpayment),
          });
        });
      });

    importStatementData2(csr, projectDocument, statementsCollection).then(() => {
      projectDocument
        .update({
          account,
          supplyCompany,
          deliveryCompany,
        })
        .catch(error => {
          console.log(error, "attempting to update projectDocument");
        });
      resolve(csr);
    });
  });
}

export function importStatementData(
  csr: ICSRFile,
  projectDocument: DocumentReference,
  statementsCollection: CollectionReference
) {
  csr.account.statements.statement.forEach(statement => {
    statementsCollection.add({
      statementDate: Date.now(),
      serviceStartDate: Date.now(),
      serviceEndDate: Date.now(),
      yearsWithSolar: 0,
      creditBalance: 0,
      totalAmountDue: 0,
      storagePath: "",
      filename: "",
      uploaded: false,
    });
  });
}

export function importStatementData2(
  csr: ICSRFile,
  projectDocument: DocumentReference,
  statements: CollectionReference
): Promise<ICSRFile> {
  return new Promise<ICSRFile>(resolve => {
    const dateTimeFormat = "M/DD/YYYY hh:mm:ss AMPM";
    const billAmountDue: IBillAmountDue[] = [];
    csr.account.statements.statement.map((statement, index, statementArray) => {
      let gridUsage: number = 0;
      let solarUsage: number = 0;
      let priorGridReading: number = 0;
      let priorSolarReading: number = 0;
      const serviceEndDate = moment(
        statement.gridreading._attributes.readdate,
        dateTimeFormat
      ).valueOf();
      let serviceStartDate = serviceEndDate;
      if (index > 0) {
        serviceStartDate = moment(
          statementArray[index - 1].gridreading._attributes.readdate,
          dateTimeFormat
        ).valueOf();
        priorGridReading = Number(statementArray[index - 1].gridreading._attributes.reading);
        priorSolarReading = Number(statementArray[index - 1].solarreading._attributes.reading);
        gridUsage =
          Number(statementArray[index].gridreading._attributes.reading) -
          Number(statementArray[index - 1].gridreading._attributes.reading);
        solarUsage =
          Number(statementArray[index].solarreading._attributes.reading) -
          Number(statementArray[index - 1].solarreading._attributes.reading);
      }
      if (index === 0) {
        const solarReading: number = Number(statement.solarreading._attributes.reading);
        const gridReading: number = Number(statement.gridreading._attributes.reading);
        let credits: number = solarReading - gridReading;
        if (credits < 0) {
          credits = 0;
        }
        statement._attributes.solarCredits = credits;
      } else {
        let credits: number =
          solarUsage + statementArray[index - 1]._attributes.solarCredits - gridUsage;
        if (credits < 0) {
          credits = 0;
        }
        statement._attributes.solarCredits = credits;
      }
      const statementDateFormat = "MM/DD/YYYY h:mm:ss AMPM";
      let totalAmountDue: number = 0.0;
      setTimeout(() => {
        statements
          .add({
            serviceStartDate,
            serviceEndDate,
            yearsWithSolar: Number(statement._attributes.NumberYearsSolar),
            statementDate: moment(
              statement._attributes.statementdate,
              statementDateFormat
            ).valueOf(),
            creditBalance: 0,
            totalAmountDue: 0,
            storagePath: "",
            filename: "",
            uploaded: false,
          })
          .then(doc => {
            projectDocument
              .collection("advancedSingleRateStatements")
              .doc(doc.id)
              .set({
                gridReadDate: moment(
                  statement.gridreading._attributes.readdate,
                  dateTimeFormat
                ).valueOf(),
                gridReading: Number(statement.gridreading._attributes.reading),
                solarReadDate: moment(
                  statement.solarreading._attributes.readdate,
                  dateTimeFormat
                ).valueOf(),
                solarReading: Number(statement.solarreading._attributes.reading),
                priorGridReadDate: serviceStartDate,
                priorGridReading,
                priorSoalrReadDate: serviceStartDate,
                priorSolarReading,
                solarGenerated: Number(statement._attributes.solargenerated),
                creditBalance: statement._attributes.solarCredits,
              })
              .then(() => {
                const advancedStatementDocument = projectDocument
                  .collection("advancedSingleRateStatements")
                  .doc(doc.id);
                const serviceChargesCollection = advancedStatementDocument.collection(
                  "serviceCharges"
                );
                if (statement.supply.servicecharge) {
                  if (statement.supply.servicecharge.length) {
                    statement.supply.servicecharge.map(serviceCharge => {
                      const isCustomerServiceCharge = getBoolean(
                        serviceCharge._attributes.iscustomerservicecharge
                      );
                      if (isCustomerServiceCharge) {
                        serviceChargesCollection.add({
                          timestamp: moment().valueOf(),
                          description: serviceCharge._attributes.servicelabel,
                          value: Number(serviceCharge._attributes.totalservicecharge),
                          unit: "Cost",
                          rate: 1.0,
                          totalCharge: Number(serviceCharge._attributes.totalservicecharge),
                        });
                        totalAmountDue += Number(serviceCharge._attributes.totalservicecharge);
                      } else {
                        serviceChargesCollection.add({
                          timestamp: moment().valueOf(),
                          description: serviceCharge._attributes.servicelabel,
                          value: Number(serviceCharge._attributes.servicecharge),
                          unit: "kWh",
                          rate: Number(serviceCharge._attributes.servicerate),
                          totalCharge:
                            Number(serviceCharge._attributes.servicecharge) *
                            Number(serviceCharge._attributes.servicerate),
                        });
                        totalAmountDue +=
                          Number(serviceCharge._attributes.servicecharge) *
                          Number(serviceCharge._attributes.servicerate);
                      }
                    });
                  } else {
                    const anyCharge: any = statement.supply.servicecharge as any;
                    const charge: ISingleServiceCharge = anyCharge as ISingleServiceCharge;
                    const isCustomerServiceCharge = getBoolean(
                      charge._attributes.iscustomerservicecharge
                    );
                    charge._attributes.servicelabel = charge._attributes.servicelabel
                      ? charge._attributes.servicelabel
                      : "";
                    if (isCustomerServiceCharge) {
                      serviceChargesCollection.add({
                        timestamp: moment().valueOf(),
                        description: charge._attributes.servicelabel,
                        value: Number(charge._attributes.totalservicecharge),
                        unit: "Cost",
                        rate: 1.0,
                        totalCharge: Number(charge._attributes.totalservicecharge),
                      });
                      totalAmountDue += Number(charge._attributes.totalservicecharge);
                    } else {
                      serviceChargesCollection.add({
                        timestamp: moment().valueOf(),
                        description: charge._attributes.servicelabel,
                        value: Number(charge._attributes.servicecharge),
                        unit: "kWh",
                        rate: Number(charge._attributes.servicerate),
                        totalCharge:
                          Number(charge._attributes.servicecharge) *
                          Number(charge._attributes.servicerate),
                      });
                      totalAmountDue +=
                        Number(charge._attributes.servicecharge) *
                        Number(charge._attributes.servicerate);
                    }
                  }
                }
                if (statement.delivery.servicecharge) {
                  if (statement.delivery.servicecharge.length) {
                    statement.delivery.servicecharge.map(serviceCharge => {
                      const isCustomerServiceCharge = getBoolean(
                        serviceCharge._attributes.iscustomerservicecharge
                      );
                      serviceCharge._attributes.servicelabel = serviceCharge._attributes
                        .servicelabel
                        ? serviceCharge._attributes.servicelabel
                        : "";
                      if (isCustomerServiceCharge) {
                        serviceChargesCollection.add({
                          timestamp: moment().valueOf(),
                          description: serviceCharge._attributes.servicelabel,
                          value: Number(serviceCharge._attributes.totalservicecharge),
                          unit: "Cost",
                          rate: 1.0,
                          totalCharge: Number(serviceCharge._attributes.totalservicecharge),
                        });
                        totalAmountDue += Number(serviceCharge._attributes.totalservicecharge);
                      } else {
                        serviceChargesCollection.add({
                          timestamp: moment().valueOf(),
                          description: serviceCharge._attributes.servicelabel,
                          value: Number(serviceCharge._attributes.servicecharge),
                          unit: "kWh",
                          rate: Number(serviceCharge._attributes.servicerate),
                          totalCharge:
                            Number(serviceCharge._attributes.servicecharge) *
                            Number(serviceCharge._attributes.servicerate),
                        });
                        totalAmountDue +=
                          Number(serviceCharge._attributes.servicecharge) *
                          Number(serviceCharge._attributes.servicerate);
                      }
                    });
                  } else {
                    const anyCharge: any = statement.delivery.servicecharge;
                    const charge: ISingleServiceCharge = anyCharge as ISingleServiceCharge;
                    const isCustomerServiceCharge = getBoolean(
                      charge._attributes.iscustomerservicecharge
                    );
                    charge._attributes.servicelabel = charge._attributes.servicelabel
                      ? charge._attributes.servicelabel
                      : "";
                    if (isCustomerServiceCharge) {
                      serviceChargesCollection.add({
                        timestamp: moment().valueOf(),
                        description: charge._attributes.servicelabel,
                        value: Number(charge._attributes.totalservicecharge),
                        unit: "Cost",
                        rate: 1.0,
                        totalCharge: Number(charge._attributes.totalservicecharge),
                      });
                      totalAmountDue += Number(charge._attributes.totalservicecharge);
                    } else {
                      serviceChargesCollection.add({
                        timestamp: moment().valueOf(),
                        description: charge._attributes.servicelabel,
                        value: Number(charge._attributes.servicecharge),
                        unit: "kWh",
                        rate: Number(charge._attributes.servicerate),
                        totalCharge:
                          Number(charge._attributes.servicecharge) *
                          Number(charge._attributes.servicerate),
                      });
                      totalAmountDue +=
                        Number(charge._attributes.servicecharge) *
                        Number(charge._attributes.servicerate);
                    }
                  }
                }
                billAmountDue.push({ id: doc.id, totalAmountDue });
              })
              .then(() => {
                projectDocument
                  .collection("simpleSingleRateStatements")
                  .doc(doc.id)
                  .set({
                    gridUsage,
                    solarUsage,
                    solarGenerated: Number(statement._attributes.solargenerated),
                    lastMonthCredit: statement._attributes.solarCredits,
                  })
                  .then(() => {
                    billAmountDue.forEach(bill => {
                      projectDocument
                        .collection("statements")
                        .doc(bill.id)
                        .update({ totalAmountDue: bill.totalAmountDue });
                    });
                  });
              });
          });
      });
    }, TIMEOUT_SHORT);
    resolve(csr);
  });
}

function getBoolean(value: string): boolean {
  if (value === "True") {
    return true;
  } else if (value === "False") {
    return false;
  }
  return false;
}

function calculateAverageEstimatedPower(
  productionAnniveraary: number,
  cumulativeEstimatedPower: number
): number {
  if (productionAnniveraary <= 0) {
    return 0;
  }
  if (cumulativeEstimatedPower <= 0) {
    return 0;
  }
  const result = cumulativeEstimatedPower / (productionAnniveraary * 12);
  if (isNaN(result)) {
    return 0;
  }
  return result;
}
function calculateAverageGuaranteedPower(
  productionAnniversary: number,
  cumulativeGuaranteedPower: number
): number {
  if (productionAnniversary <= 0) {
    return 0;
  }
  if (cumulativeGuaranteedPower <= 0) {
    return 0;
  }
  const result = cumulativeGuaranteedPower / (productionAnniversary * 12);
  if (isNaN(result)) {
    return 0;
  }
  return result;
}

export {
  IAccountInformationAttributes,
  IAccountInformation,
  ICSRAccount,
  IPowerAttributes,
  IPower,
  ICSRFile as ICSR,
};



// WEBPACK FOOTER //
// ./src/pages/projects/project/statements/CulinSolarTrackerImporter.tsx